'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { CrmConnectWelcomeModal } from './CrmConnectWelcomeModal';
import { CrmConnectCompleteModal } from './CrmConnectCompleteModal';
import { CRM_CONNECT_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = CRM_CONNECT_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * CrmConnectTourOverlay - Renders the CRM Connect tour step UI.
 *
 * Only renders when the active tour is 'crm-connect'. Navigates the user
 * to Integrations to walk through connecting Agency Zoom via Zapier.
 */
export function CrmConnectTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isCrmTour = activeTourId === 'crm-connect';

  // Auto-advance polling
  useTourAutoAdvance(isCrmTour, currentStep, nextStep);

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation
  useEffect(() => {
    if (!isCrmTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // All highlight steps happen on the integrations page
    const integrationSteps = [
      'highlight_az_connect',
      'highlight_az_find_creds',
      'highlight_az_zap_fields',
    ];
    if (integrationSteps.includes(currentStep.id)) {
      navigatedForStepRef.current = currentStep.id;
      router.push('/settings?section=integrations');
      reduxLogger.info(`[CrmConnectTour] Navigating to Integrations for step: ${currentStep.id}`);
    }
  }, [isCrmTour, currentStep?.id, currentStep, router]);

  const handleWelcomeComplete = useCallback(() => {
    router.push('/settings?section=integrations');
    reduxLogger.info('[CrmConnectTour] Navigating to Integrations');
    nextStep();
  }, [router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('crm-connect');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isCrmTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'crm_welcome' && <CrmConnectWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'crm_complete' && <CrmConnectCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
